import axios, {AxiosResponse} from "axios";
import Nanobus from "nanobus";

export default class AccountComposer {

    element: HTMLElement
    GenerateForm: GenerateForm
    ComposerTable: ComposerTable
    JsonToggle: ComposerJson

    constructor(section: HTMLElement) {
        this.element = section;
        this.GenerateForm = new GenerateForm(section.querySelector<HTMLFormElement>('#generateComposerKey')!);
        this.ComposerTable = new ComposerTable(section.querySelector<HTMLElement>('.composer-token-table')!);
        this.JsonToggle = new ComposerJson(section);

        this.init();
    }

    init() {
        document.querySelectorAll<HTMLElement>('.composer-key-row').forEach(element => {
            new ComposerKey(element);
        });

        this.GenerateForm.events.on('generated', e => {
            this.ComposerTable.addComposerKey(e.key);
            this.ComposerTable.addCodeBlockRow(e.codeRow);
        });

    }
}

class ComposerJson {
    element: HTMLElement

    constructor(element: HTMLElement) {
        this.element = element;
        this.initEvents();
    }

    toggleComposerJson() {
        const el = this.element.querySelector('[data-composer-json]')!;

        el.classList.contains('-show')
            ? el.classList.remove('-show')
            : el.classList.add('-show')
    }

    initEvents() {
        this.element.querySelectorAll<HTMLElement>('[data-toggle-composer-json]').forEach(el => {
            el.addEventListener('click', e => {
                e.preventDefault();
                this.toggleComposerJson()

                const currentText = el.textContent;
                if (currentText) {
                    el.textContent = el.dataset.toggleText!;
                    el.dataset.toggleText = currentText;
                }
            })
        });
    }

}

class ComposerTable {

    element: HTMLElement

    constructor(element: HTMLElement) {
        this.element = element;
    }

    addComposerKey(ComposerKey: ComposerKey) {
        this.element.querySelector('tbody')?.append(ComposerKey.getElement());
    }

    addCodeBlockRow(row: HTMLTableRowElement) {
        this.element.querySelector('tbody')?.append(row);
    }

}

class ComposerKey {

    element: HTMLElement

    constructor(element: HTMLElement) {
        this.element = element;
        this.initEvents();
    }

    getElement() {
        return this.element;
    }

    toggleAuth() {
        const el = this.element.nextElementSibling!;

        el.classList.contains('-show')
            ? el.classList.remove('-show')
            : el.classList.add('-show')
    }

    initEvents() {
        this.element.querySelectorAll<HTMLElement>('[data-toggle-auth]').forEach(el => {
            el.addEventListener('click', e => {
                e.preventDefault();
                this.toggleAuth();

                const toggleText = el.dataset.toggleText;
                if (toggleText !== undefined) {
                    const currentText = el.textContent ?? '';
                    el.textContent = toggleText;
                    el.dataset.toggleText = currentText;
                }
            });
        });

        this.element.querySelectorAll<HTMLElement>('[data-delete]').forEach(el => {
            el.addEventListener('click', e => {
                e.preventDefault();

                if (window.confirm('Are you sure you want to delete this Composer API token? Anyone using this token loses access.')) {
                    deleteKey(el.dataset.delete!).then((response: AxiosResponse<any>) => {
                        if (response.data.success) {
                            this.remove();
                        } else {
                            this.setMessage(response.data.data);
                        }
                    });
                }
            });
        });
    }

    remove() {
        this.element.remove();
    }

    setMessage(message: string, type = '-error') {
        this.element.querySelector('.composer-key__message')!.innerHTML = message
            ? `<div class="composer-key__message__content ${type}">${message}</div>`
            : '';
    }

}

class GenerateForm {
    form: HTMLFormElement
    events: Nanobus
    button: HTMLElement
    cancel: HTMLElement
    buttonLabel: string

    constructor(form: HTMLFormElement) {
        this.form = form;
        this.events = new Nanobus();
        this.initEvents();
        this.button = form.querySelectorAll<HTMLElement>('[data-toggle]').item(0);
        this.cancel = form.querySelectorAll<HTMLElement>('[data-toggle-cancle]').item(0);
        this.buttonLabel = this.button.textContent ?? '';
    }

    toggleForm(enabled = true) {
        if (enabled) {
            this.form.classList.remove('-hidden');
            this.button.textContent = 'Save';
        } else {
            this.form.classList.add('-hidden');
            this.button.textContent = this.buttonLabel
        }
    }

    isFormActive() {
        return !this.form.classList.contains('-hidden');
    }

    toggleInputs(enabled = true) {
        this.form.querySelectorAll<HTMLInputElement>('.composer-form-inputs button').forEach(inp => inp.disabled = !enabled);
    }

    resetForm() {
        this.form.querySelector<HTMLInputElement>('[name=description]')!.value = '';
        this.toggleInputs();
    }

    submitForm() {
        let data = new FormData(this.form);
        this.toggleInputs(false);

        generateKey(data).then((response: AxiosResponse<any>) => {
            this.toggleForm(false);

            let template = document.createElement('template');
            template.innerHTML = response.data.data.markup.trim();

            let fragment = template.content;
            let codeRow = fragment.lastElementChild as HTMLTableRowElement;
            let composerKey = new ComposerKey(fragment.firstElementChild as HTMLElement);

            this.events.emit('generated', {key: composerKey, codeRow: codeRow})
        });
    }

    initEvents() {

        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
        });

        this.form.querySelectorAll('[data-toggle-cancel]').forEach(el => {
            el.addEventListener('click', e => {
                e.preventDefault();
                this.toggleForm(false);
            })
        });

        this.form.querySelectorAll('[data-toggle]').forEach(el => {
            el.addEventListener('click', e => {
                e.preventDefault();
                if (this.isFormActive()) {
                    this.submitForm();
                } else {
                    this.toggleForm();
                    this.resetForm();
                    this.form.querySelector<HTMLInputElement>('[name=description]')?.focus();
                }
            })
        });
    }
}

const deleteKey = (id: string) => {
    let data = new FormData();
    data.set('id', id);
    data.set('action', 'acp_delete_composer_key');

    return axios({
        url: codepress.ajaxurl,
        method: 'post',
        data: data
    });
}

const generateKey = (data = new FormData()) => {
    return axios({
        url: codepress.ajaxurl,
        method: 'post',
        data: data
    });
}